import { getLocaleRules, isDisplayableNumber, type LocaleInput } from './locale';

/**
 * A number as the user reads it: the locale's decimal and thousands separators, always
 * `decimals` places. Anything that is not a number shows as `fallback`.
 */
export function formatNumberByLocale(
    value: number | null | undefined,
    locale: LocaleInput,
    decimals = 2,
    fallback = '-'
): string {
    if (!isDisplayableNumber(value)) {
        return fallback;
    }

    const rules = getLocaleRules(locale);
    const [integerPart, decimalPart] = value.toFixed(decimals).split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, rules.thousandsSeparator);

    return decimals > 0
        ? `${formattedInteger}${rules.decimalSeparator}${decimalPart}`
        : formattedInteger;
}

/**
 * Like `formatNumberByLocale`, but with only as many places as the value needs, up to
 * `maxDecimals`: "1,234", "1,234.5". For free-form figures, such as an edited field's old and
 * new value, where a fixed count would pad whole numbers with zeros.
 */
export function formatNumberMaxDecimalsByLocale(
    value: number | null | undefined,
    locale: LocaleInput,
    maxDecimals = 3,
    fallback = '-'
): string {
    if (!isDisplayableNumber(value)) {
        return fallback;
    }

    const rounded = Number(value.toFixed(maxDecimals));
    let decimals = 0;
    while (decimals < maxDecimals && Number(value.toFixed(decimals)) !== rounded) {
        decimals += 1;
    }
    return formatNumberByLocale(value, locale, decimals, fallback);
}

/**
 * A number for machines rather than people: CSV and XLSX cells, API parameters, input values,
 * chart data and file names. Always `.` and never grouped, whatever the UI language, so the
 * value parses the same wherever it is read back. A missing value stays missing, for the caller
 * to fill (`?? ''` in a CSV cell).
 */
export function formatMachineNumber(value: number, decimals: number): string;
export function formatMachineNumber(
    value: number | null | undefined,
    decimals: number
): string | undefined;
export function formatMachineNumber(
    value: number | null | undefined,
    decimals: number
): string | undefined {
    return value == null ? undefined : value.toFixed(decimals);
}

/** `value` rounded to `decimals` places, kept a number for an export cell or chart point. */
export function roundToDecimals(value: number, decimals: number): number {
    // Math.round for whole numbers, as the exports always did: toFixed(0) rounds -2.5 to -3.
    if (decimals === 0) return Math.round(value);
    return Number(value.toFixed(decimals));
}
